import { useEffect, useMemo, useState } from "react";
import type { AgeGroupPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Check } from "lucide-react";

export default function AdminAgeGroupsPage() {
  const [ageGroups, setAgeGroups] = useState<AgeGroupPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getAllAgeGroups()
      .then((r) => setAgeGroups(r.age_groups))
      .catch(() => setError("Failed to load age groups"));
  }, []);

  const bySport = useMemo(() => {
    if (!ageGroups) return [];
    const map = new Map<string, { sportId: string; sportName: string; groups: AgeGroupPublic[] }>();
    for (const ag of ageGroups) {
      let entry = map.get(ag.sport_id);
      if (!entry) {
        entry = { sportId: ag.sport_id, sportName: ag.sport_name, groups: [] };
        map.set(ag.sport_id, entry);
      }
      entry.groups.push(ag);
    }
    return [...map.values()].sort((a, b) => a.sportName.localeCompare(b.sportName));
  }, [ageGroups]);

  return (
    <div
      className="flex flex-col gap-6"
      data-page="admin-age-groups"
      data-state={ageGroups === null ? "loading" : error ? "error" : "ready"}
    >
      <Breadcrumbs items={[{ label: "Age Groups & Match Durations" }]} />
      <div>
        <h1 className="text-2xl font-semibold">Age Groups & Match Durations</h1>
        <p className="text-muted-foreground">
          Configure default match half-times per sport and age group. These defaults are used when creating new matches.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!ageGroups && !error && <p className="text-muted-foreground">Loading...</p>}

      {bySport.map((sport) => (
        <SportSection
          key={sport.sportId}
          sportName={sport.sportName}
          groups={sport.groups}
          onUpdate={(updated) => {
            setAgeGroups((prev) =>
              prev ? prev.map((ag) => ag.id === updated.id ? updated : ag) : prev,
            );
          }}
        />
      ))}
    </div>
  );
}

function SportSection({ sportName, groups, onUpdate }: {
  sportName: string;
  groups: AgeGroupPublic[];
  onUpdate: (ag: AgeGroupPublic) => void;
}) {
  return (
    <div className="rounded-lg border border-border">
      <div className="border-b bg-muted/40 px-4 py-2.5">
        <h2 className="text-sm font-semibold">{sportName}</h2>
      </div>
      <div className="divide-y divide-border">
        {groups.map((ag) => (
          <AgeGroupRow key={ag.id} ageGroup={ag} onUpdate={onUpdate} />
        ))}
      </div>
    </div>
  );
}

function AgeGroupRow({ ageGroup, onUpdate }: {
  ageGroup: AgeGroupPublic;
  onUpdate: (ag: AgeGroupPublic) => void;
}) {
  const canEdit = ageGroup._actions.includes("edit");
  const [firstHalf, setFirstHalf] = useState(ageGroup.default_first_half_mins);
  const [breakMins, setBreakMins] = useState(ageGroup.default_break_mins);
  const [secondHalf, setSecondHalf] = useState(ageGroup.default_second_half_mins);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const changed = firstHalf !== ageGroup.default_first_half_mins
    || breakMins !== ageGroup.default_break_mins
    || secondHalf !== ageGroup.default_second_half_mins;

  async function save() {
    setSaving(true);
    try {
      const res = await api.updateAgeGroup(ageGroup.id, {
        default_first_half_mins: firstHalf,
        default_break_mins: breakMins,
        default_second_half_mins: secondHalf,
      });
      onUpdate(res.age_group);
      setFirstHalf(res.age_group.default_first_half_mins);
      setBreakMins(res.age_group.default_break_mins);
      setSecondHalf(res.age_group.default_second_half_mins);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* silent */ }
    setSaving(false);
  }

  const total = firstHalf + breakMins + secondHalf;

  return (
    <div
      className="flex items-center gap-4 px-4 py-2.5"
      data-item="age-group"
      data-item-id={ageGroup.id}
      data-age-group-name={ageGroup.name}
    >
      <div className="w-32 shrink-0">
        <span className="text-sm font-medium">{ageGroup.name}</span>
        <span className="ml-2 text-xs text-muted-foreground">{ageGroup.id}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-xs text-muted-foreground w-8 text-right">Half</span>
        <Input
          type="number" min={1} max={60} value={firstHalf} disabled={!canEdit}
          onChange={(e) => { const v = Number(e.target.value); setFirstHalf(v); setSecondHalf(v); }}
          className="h-7 w-16 text-center text-xs"
          data-field="half_mins"
        />
        <span className="text-xs text-muted-foreground">× 2</span>
        <span className="text-xs text-muted-foreground">+</span>
        <span className="text-xs text-muted-foreground w-8 text-right">Break</span>
        <Input
          type="number" min={0} max={30} value={breakMins} disabled={!canEdit}
          onChange={(e) => setBreakMins(Number(e.target.value))}
          className="h-7 w-16 text-center text-xs"
          data-field="break_mins"
        />
        <span className="text-xs text-muted-foreground">min</span>
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-16" data-field="total">{total} min</span>
      <div className="w-20">
        {canEdit && changed && (
          <Button variant="default" size="sm" onClick={save} disabled={saving} data-action="save" className="h-7 text-xs">
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
        {saved && !changed && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-field="saved-indicator">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        )}
      </div>
    </div>
  );
}
